import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from "react";
export { DataTable, EmptyState, PageHeader, StatusPill as StatusBadge } from "@/components/common";

export function Button({ variant = "secondary", className = "", ...props }: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "danger" | "link";
}) {
  const styles = { primary: "btn-primary", secondary: "btn-secondary", danger: "btn-secondary border-bad text-bad", link: "text-accent underline" };
  return <button type="button" className={`${styles[variant]} focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${className}`} {...props} />;
}

export function SectionCard({ title, summary, children, collapsible = false, defaultOpen = true }: {
  title: string; summary?: string; children: ReactNode; collapsible?: boolean; defaultOpen?: boolean;
}) {
  if (collapsible) return <details className="card" open={defaultOpen || undefined}>
    <summary className="cursor-pointer text-sm font-semibold focus-visible:outline-2 focus-visible:outline-accent">{title}
      {summary && <span className="ml-2 font-normal text-muted">{summary}</span>}</summary>
    <div className="mt-3">{children}</div>
  </details>;
  return <section className="card"><h2 className="text-sm font-semibold">{title}</h2>
    {summary && <p className="mt-1 text-xs text-muted">{summary}</p>}
    <div className="mt-3">{children}</div></section>;
}

export function Field({ label, hint, error, children }: { label: string; hint?: string; error?: string; children: ReactNode }) {
  return <label className="block text-sm"><span className="font-medium">{label}</span>{children}
    {hint && <span className="mt-1 block text-xs text-muted">{hint}</span>}
    {error && <span className="mt-1 block text-xs text-bad" role="alert">{error}</span>}
  </label>;
}

export function Tabs<T extends string>({ value, items, onChange, label }: {
  value: T; items: Array<{ value: T; label: string }>; onChange: (value: T) => void; label: string;
}) {
  return <div className="flex flex-wrap gap-1 border-b border-border" role="tablist" aria-label={label}>
    {items.map((item) => <Button key={item.value} role="tab" aria-selected={value === item.value}
      variant="link" className={`border-b-2 px-4 py-2 text-sm no-underline ${value === item.value ? "border-accent text-accent" : "border-transparent text-muted"}`}
      onKeyDown={(event) => {
        if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
        event.preventDefault();
        const current = items.findIndex((tab) => tab.value === item.value);
        const next = items[(current + (event.key === "ArrowRight" ? 1 : -1) + items.length) % items.length];
        onChange(next.value);
        const tabs = event.currentTarget.parentElement?.querySelectorAll<HTMLElement>("[role=tab]");
        tabs?.[(current + (event.key === "ArrowRight" ? 1 : -1) + items.length) % items.length]?.focus();
      }}
      onClick={() => onChange(item.value)}>{item.label}</Button>)}
  </div>;
}

export function Checkbox(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} type="checkbox" className={`h-4 w-4 accent-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${props.className || ""}`} />;
}
export function Radio(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} type="radio" className={`h-4 w-4 accent-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${props.className || ""}`} />;
}
export function Toggle({ checked, onChange, disabled, label }: {
  checked: boolean; onChange: (checked: boolean) => void; disabled?: boolean; label: string;
}) {
  return <button type="button" role="switch" aria-checked={checked} aria-label={label} disabled={disabled}
    className={`inline-flex h-6 w-11 items-center border border-border p-0.5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-50 ${checked ? "bg-accent" : "bg-panel"}`}
    onClick={() => onChange(!checked)}><span className={`h-4 w-4 bg-fg transition-transform ${checked ? "translate-x-5" : ""}`} /></button>;
}
